<script lang="ts" setup>
  import { computed, toRef, useCssModule } from 'vue';

  import usePaginationStats from '../../composables/usePaginationStats/usePaginationStats';
  import { t } from '../../locale';
  import Icon from '../Icon/Icon.vue';

  defineOptions({
    name: 'll-paginate',
  });

  export interface PaginateProps {
    /**
     * Current page number
     */
    currentPage?: number;
    /**
     * Total number of items in list
     */
    listLength?: number;
    /**
     * Number of items per page
     */
    pageSize?: number;
    /*
     * Optional override for the next button disabled state
     */
    isNextDisabled?: boolean;
    /**
     * Variant of the pagination component
     */
    variant?: 'standard' | 'stats';
  }

  const props = withDefaults(defineProps<PaginateProps>(), {
    currentPage: 1,
    listLength: 1,
    pageSize: 50,
    isNextDisabled: false,
    variant: 'standard',
  });

  const emit = defineEmits<{
    /**
     * Emitted when the page number changes.
     */
    (e: 'set-page', pageNumber: number): void;
  }>();

  const classes = useCssModule();

  const pageCount = computed(() => Math.ceil(props.listLength / props.pageSize));

  const pageStats =
    props.variant === 'stats'
      ? usePaginationStats({
          currentPage: toRef(() => props.currentPage),
          pageSize: toRef(() => props.pageSize),
          totalItems: toRef(() => props.listLength),
        })
      : null;

  /**
   * Emits current page to list view.
   */
  function updatePage(currentPage: number) {
    emit('set-page', currentPage);
  }

  /**
   * Sets current page to next page
   */
  function next() {
    if (props.currentPage < pageCount.value) {
      updatePage(props.currentPage + 1);
    }
  }

  /**
   * Sets current page to previous page
   */
  function prev() {
    if (props.currentPage > 1) {
      updatePage(props.currentPage - 1);
    }
  }

  /**
   * Determines whether to show page
   * @returns {boolean} - should show page in pagination component
   */
  function shouldShowPage(page: number): boolean {
    const pageDiff = Math.abs(props.currentPage - page);

    if (
      pageCount.value < 10 ||
      page === 1 ||
      page === pageCount.value ||
      (props.currentPage < 6 && page < 8) ||
      (pageCount.value - props.currentPage < 5 && pageCount.value - page < 7) ||
      pageDiff < 3
    ) {
      return true;
    }

    return false;
  }

  /**
   * Determines whether to show the leading ellipsis
   * @returns {boolean} should show or not
   */
  function shouldShowFirstEllipsis(page: number): boolean {
    return pageCount.value > 9 && props.currentPage > 5 && page === 2;
  }

  /**
   * Determines whether to show the trailing ellipsis
   * @returns {boolean} should show or not
   */
  function shouldShowLastEllipsis(page: number): boolean {
    return pageCount.value > 9 && pageCount.value - props.currentPage > 4 && page === pageCount.value - 1;
  }
</script>

<template>
  <div class="stash-paginate flex justify-center" data-test="stash-paginate">
    <ul class="stash-paginate__list flex items-center" :class="classes.pagination" data-test="stash-paginate|list">
      <button
        :aria-label="t('ll.previous')"
        data-test="stash-paginate|prev-page"
        :class="classes.prev"
        :disabled="props.currentPage === 1"
        @click="prev"
      >
        <Icon size="small" name="chevron-left" />
      </button>
      <span v-if="props.variant === 'stats'" class="mx-3 text-ice-900">
        {{ t('ll.pageStats', pageStats) }}
      </span>
      <template v-for="page in pageCount" v-else>
        <button
          v-if="shouldShowFirstEllipsis(page)"
          :key="`${page}-ellipsis`"
          class="pointer-events-none"
          data-test="stash-paginate|first-ellipsis"
          tabindex="-1"
        >
          &#x2026;
        </button>
        <button
          v-if="shouldShowPage(page)"
          :key="page"
          data-test="stash-paginate|page-number"
          :class="{ [classes['is-active']]: props.currentPage === page }"
          @click="updatePage(page)"
        >
          {{ page }}
        </button>
        <button
          v-if="shouldShowLastEllipsis(page)"
          :key="`${page}-ellipsis`"
          class="pointer-events-none"
          data-test="stash-paginate|last-ellipsis"
          tabindex="-1"
        >
          &#x2026;
        </button>
      </template>
      <button
        :aria-label="t('ll.next')"
        data-test="stash-paginate|next-page"
        :class="classes.next"
        :disabled="props.isNextDisabled || props.currentPage === pageCount"
        @click="next"
      >
        <Icon size="small" class="rotate-180" name="chevron-left" />
      </button>
    </ul>
  </div>
</template>

<style module>
  @reference "../../../styles/main.css";

  @layer utilities {
    .pagination {
      /* Todo: We shouldn't really be making assumptions about the outer spacing of the pagination component */
      margin-top: --spacing(12);
      z-index: 0;

      a,
      button {
        @apply border border-solid border-ice-200;
        align-items: center;
        background-color: var(--color-white);
        color: var(--color-blue-500);
        cursor: pointer;
        display: flex;
        height: var(--height-input);
        justify-content: center;
        margin: 0 0 0 -1px;
        width: var(--height-input);

        &.prev {
          border-bottom-left-radius: var(--radius-sm);
          border-top-left-radius: var(--radius-sm);
        }

        &.next {
          border-bottom-right-radius: var(--radius-sm);
          border-top-right-radius: var(--radius-sm);
        }

        &:focus {
          outline: none;
        }

        &:hover {
          text-decoration: none;
        }

        &.is-active {
          background-color: var(--color-blue-500);
          color: var(--color-white);
          pointer-events: none;
          z-index: var(--z-index-control);
        }

        &:disabled {
          cursor: not-allowed;
          opacity: 0.5;
        }

        &:not(:disabled):hover,
        &:not(:disabled):focus,
        &.is-active {
          @apply border border-solid border-blue-500;
          position: relative;
        }

        &.is-active:focus {
          @apply bg-blue-700 border-blue-700;
        }
      }
    }
  }
</style>
